'use client';

import { RAIL_MENU_ROW, RAIL_MENU_ACTIVE } from './rail-menu-styles';

import { Box, useBreakpointValue } from '@chakra-ui/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getPrimarySection } from './primary-navigation';
import { useAppRail, useAppRailOverlay } from './AppRailContext';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const AppShellHeaderContext = createContext<{
  host: HTMLDivElement | null;
  railHost: HTMLDivElement | null;
  toolbarHost: HTMLDivElement | null;
  setRailHost: (element: HTMLDivElement | null) => void;
  setToolbarHost: (element: HTMLDivElement | null) => void;
  setHost: (element: HTMLDivElement | null) => void;
} | null>(null);

export function AppShellHeaderProvider({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [railHost, setRailHost] = useState<HTMLDivElement | null>(null);
  const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
  const value = useMemo(
    () => ({ host, setHost, railHost, setRailHost, toolbarHost, setToolbarHost }),
    [host, railHost, toolbarHost]
  );
  return <AppShellHeaderContext.Provider value={value}>{children}</AppShellHeaderContext.Provider>;
}

export function AppShellHeaderSlot() {
  const context = useContext(AppShellHeaderContext);
  return (
    <Box
      ref={context?.setHost}
      data-section="appShellChatHeader"
      position="relative"
      left="auto"
      transform="none"
      flex="1"
      mx={{ base: '1', lg: '0' }}
      maxW="full"
      minW={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
    />
  );
}

export function AppShellHeaderPortal({ children }: { children: ReactNode }) {
  const context = useContext(AppShellHeaderContext);
  // Session identity belongs to the top bar; section navigation belongs to the rail.
  return context?.host ? createPortal(children, context.host) : null;
}

export function useAppShellRailHeaders() {
  const context = useContext(AppShellHeaderContext);
  const pathname = usePathname();
  const params = useSearchParams();
  const desktop = useBreakpointValue({ base: false, lg: true }) ?? false;
  const section = getPrimarySection(pathname, params);
  const railEligible = section !== 'data' && section !== 'library';
  return {
    railEligible,
    inRail: desktop && railEligible && Boolean(context?.toolbarHost),
    toolbarHost: context?.toolbarHost,
  };
}

export function AppShellRailHeaderSlots() {
  const context = useContext(AppShellHeaderContext);
  const pathname = usePathname();
  const params = useSearchParams();
  const taskMenu = pathname === '/app/workspace' && params.get('mode') === 'tasks';
  return (
    <Box
      data-section="railSectionControls"
      minH={taskMenu ? '294px' : undefined}
      flexShrink={0}
      css={{
        '& > div:empty': { display: 'none' },
        '& > div:not(:empty)': { padding: '8px 0 12px' },
        '& [data-section="appShellToolbarPrimaryRow"], & [data-section="appShellToolbarCompactUtilityRow"]':
          { flexDirection: 'column', alignItems: 'stretch' },
        '& [data-section="appShellToolbarRight"]': {
          marginLeft: 0,
          order: -1,
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
        },
        '& [data-section="appShellToolbarFilters"]': { flexBasis: 'auto', flexWrap: 'wrap' },
        '& [data-section="appShellToolbarNavigation"]': { flex: 'initial', width: '100%' },
        '& [data-section="appShellToolbarTabs"]': {
          flexDirection: 'column',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          width: '100%',
          gap: '2px',
          overflow: 'visible',
        },
        '& [data-section="appShellToolbarTabs"] > a, & [data-section="appShellToolbarTabs"] > button':
          {
            width: '100%',
            justifyContent: 'flex-start',
            height: '36px',
            minHeight: '36px',
            flexShrink: 0,
            paddingInline: RAIL_MENU_ROW.px,
            borderRadius: RAIL_MENU_ROW.borderRadius,
            fontSize: RAIL_MENU_ROW.fontSize,
            lineHeight: RAIL_MENU_ROW.lineHeight,
            fontWeight: RAIL_MENU_ROW.fontWeight,
            gap: RAIL_MENU_ROW.gap,
            color: RAIL_MENU_ROW.color,
            '&:hover': RAIL_MENU_ROW._hover,
            '&:focus-visible': RAIL_MENU_ROW._focusVisible,
            '&[aria-current="page"], &[aria-pressed="true"], &[data-active="true"]':
              RAIL_MENU_ACTIVE,
          },
        '& [data-section="appShellToolbarActions"]': { width: '100%' },
        '& [data-section="appShellToolbarActions"] > button': {
          width: '100%',
          justifyContent: 'flex-start',
          height: '36px',
          minHeight: '36px',
          flexShrink: 0,
          borderRadius: RAIL_MENU_ROW.borderRadius,
          fontSize: RAIL_MENU_ROW.fontSize,
          lineHeight: RAIL_MENU_ROW.lineHeight,
          fontWeight: RAIL_MENU_ROW.fontWeight,
        },
      }}
    >
      <Box ref={context?.setRailHost} minW={0} />
      <Box ref={context?.setToolbarHost} minW={0} />
    </Box>
  );
}

/** Portalled controls retain the rail's pointer and keyboard interaction ownership. */
export function AppShellRailContent({ children }: { children: ReactNode }) {
  const { handleRailPointerEnter, handleRailPointerLeave } = useAppRail();
  const onFocusChange = useAppRailOverlay();
  return (
    <Box
      minW={0}
      onPointerEnter={handleRailPointerEnter}
      onPointerLeave={handleRailPointerLeave}
      onFocusCapture={() => onFocusChange({ open: true })}
      onBlurCapture={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          onFocusChange({ open: false });
      }}
    >
      {children}
    </Box>
  );
}
